import { createContext, useContext, useState, useEffect, useCallback } from "react";

const LanguageContext = createContext(null);

export const LANGUAGES = [
  { code: "en", label: "English",  short: "EN" },
  { code: "hi", label: "हिन्दी",   short: "हि"  },
  { code: "te", label: "తెలుగు",   short: "తె"  },
];

const STORAGE_KEY = "jyotish-lang";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && ["en", "hi", "te"].includes(v) ? v : "en";
  });

  const setLang = useCallback((next) => {
    if (!["en", "hi", "te"].includes(next)) return;
    setLangState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) return { lang: "en", setLang: () => {} };
  return ctx;
}

// ---- Translation dictionary for static UI labels ----
// Keep keys short. Fallback = English string when key is missing.
const T = {
  en: {},
  hi: {
    "Refresh": "पुनः लोड करें",
    "Generate": "उत्पन्न करें",
    "Download PDF": "पीडीएफ डाउनलोड करें",
    "Loading…": "लोड हो रहा है…",
    "Loading...": "लोड हो रहा है…",
    "Generating…": "उत्पन्न हो रहा है…",
    "Category": "श्रेणी",
    "Topic": "विषय",
    "Focus": "केंद्र",
    "Language": "भाषा",
    "Birth Profiles": "जन्म कुंडलियाँ",
    "Native": "जातक",
    "Charts": "चार्ट",
    "Chart Views": "चार्ट दृश्य",
    "Strengths": "बल",
    "Attributes": "गुण",
    "Yogas": "योग",
    "Dasha": "दशा",
    "Day": "दिन",
    "Panchang": "पंचांग",
    "Nakshatra": "नक्षत्र",
    "Homeopathy": "होम्योपैथी",
    "Miasm": "मियाज़्म",
    "Kentian": "केंट विश्लेषण",
    "Life Span": "आयुर्दाय",
    "Red Alerts": "चेतावनी काल",
    "Numerology": "अंक ज्योतिष",
    "AI Search": "AI खोज",
    "Vim CSV": "विंशोत्तरी CSV",
    "Turning Points": "मोड़ बिंदु",
    "Muhurta": "मुहूर्त",
    "Dasha-Transit": "दशा-गोचर",
    "Remedies": "उपाय",
    "Good Alerts": "शुभ काल",
    "Full Life": "सम्पूर्ण जीवन",
    "Regenerate": "फिर से उत्पन्न करें",
    "Save": "सहेजें",
    "Cancel": "रद्द करें",
    "Delete": "हटाएँ",
    "Ask": "पूछें",
    "Question": "प्रश्न",
    "Answer": "उत्तर",
    "Insight Engine": "अंतर्दृष्टि इंजन",
    "Jyotish · Homeopathy": "ज्योतिष · होम्योपैथी",
    "Stock Market": "शेयर बाज़ार",
    "Storyboard": "स्टोरीबोर्ड",
    "Slideshow": "स्लाइडशो",
    "Holistic Report": "समग्र रिपोर्ट",
    "Case Taking": "केस लेखन",
    "Consult ₹20/min": "परामर्श ₹20/मिनट",
    "PDF": "पीडीएफ",
  },
  te: {
    "Refresh": "రిఫ్రెష్",
    "Generate": "సృష్టించండి",
    "Download PDF": "PDF డౌన్‌లోడ్",
    "Loading…": "లోడ్ అవుతోంది…",
    "Loading...": "లోడ్ అవుతోంది…",
    "Generating…": "సృష్టిస్తోంది…",
    "Category": "వర్గం",
    "Topic": "అంశం",
    "Focus": "కేంద్రం",
    "Language": "భాష",
    "Birth Profiles": "జనన వివరాలు",
    "Native": "జాతకుడు",
    "Charts": "చార్టులు",
    "Chart Views": "చార్టు వీక్షణలు",
    "Strengths": "బలములు",
    "Attributes": "గుణాలు",
    "Yogas": "యోగాలు",
    "Dasha": "దశ",
    "Day": "రోజు",
    "Panchang": "పంచాంగం",
    "Nakshatra": "నక్షత్రం",
    "Homeopathy": "హోమియోపతి",
    "Miasm": "మయాజ్మ్",
    "Kentian": "కెంట్ విశ్లేషణ",
    "Life Span": "ఆయుఃప్రమాణం",
    "Red Alerts": "హెచ్చరిక కాలం",
    "Numerology": "సంఖ్యాశాస్త్రం",
    "AI Search": "AI శోధన",
    "Vim CSV": "విమ్‌శోత్తరి CSV",
    "Turning Points": "మలుపు కాలాలు",
    "Muhurta": "ముహూర్తం",
    "Dasha-Transit": "దశ-గోచరం",
    "Remedies": "పరిహారాలు",
    "Good Alerts": "శుభ కాలం",
    "Full Life": "సంపూర్ణ జీవితం",
    "Regenerate": "మళ్ళీ సృష్టించండి",
    "Save": "సేవ్",
    "Cancel": "రద్దు",
    "Delete": "తొలగించు",
    "Ask": "అడగండి",
    "Question": "ప్రశ్న",
    "Answer": "సమాధానం",
    "Insight Engine": "అంతర్‌దృష్టి ఇంజిన్",
    "Jyotish · Homeopathy": "జ్యోతిష · హోమియోపతి",
    "Stock Market": "స్టాక్ మార్కెట్",
    "Storyboard": "స్టోరీబోర్డ్",
    "Slideshow": "స్లైడ్‌షో",
    "Holistic Report": "సమగ్ర రిపోర్ట్",
    "Case Taking": "కేసు నమోదు",
    "Consult ₹20/min": "సంప్రదింపు ₹20/నిమిషం",
    "PDF": "PDF",
  },
};

export function useT() {
  const { lang } = useLang();
  return (key) => (T[lang] && T[lang][key]) || key;
}
